'use client';

import { c as _c } from "react/compiler-runtime";
import { type ItemsRenderProps, useAuth, useGetGiftsInfinite } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { Skeleton } from '@/components/ui/app-skeleton';
import { InfiniteList } from '@/components/ui/infinite-list';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { GIFT_LIST_FIELDS } from '@/lib/sdk/projections';
import type { CompactGiftRowVariant } from './compact-gift-row';
import type { GiftRowData } from './gift-row';
import { LiveCompactGiftRow } from './live-compact-gift-row';
export interface AllGiftsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Tab the dialog opened from. The dialog does NOT switch tabs (per
   * spec FR-022) — the parent is the source of truth for the active tab.
   */
  activeTab: CompactGiftRowVariant;
}
export function AllGiftsDialog(t0) {
  const $ = _c(11);
  const {
    open,
    onOpenChange,
    activeTab
  } = t0;
  const tDialog = useTranslations("gift.allGiftsDialog");
  let t1;
  if ($[0] !== activeTab || $[1] !== tDialog) {
    t1 = activeTab === "received" ? tDialog("titleReceived") : tDialog("titleSent");
    $[0] = activeTab;
    $[1] = tDialog;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== activeTab || $[4] !== open) {
    t2 = open ? <AllGiftsDialogContent activeTab={activeTab} /> : null;
    $[3] = activeTab;
    $[4] = open;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== onOpenChange || $[7] !== open || $[8] !== t1 || $[9] !== t2) {
    t3 = <SilkSheet open={open} onOpenChange={onOpenChange} variant="detached" title={t1} size="default">{t2}</SilkSheet>;
    $[6] = onOpenChange;
    $[7] = open;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
function AllGiftsDialogContent(t0) {
  const $ = _c(43);
  const {
    activeTab
  } = t0;
  const tInbox = useTranslations("gift.inbox");
  const {
    user
  } = useAuth();
  const userId = user?.id ?? "";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [...GIFT_LIST_FIELDS];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== activeTab || $[2] !== userId) {
    t2 = {
      userId,
      kind: activeTab,
      fields: t1
    };
    $[1] = activeTab;
    $[2] = userId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = userId !== "";
  let t4;
  if ($[4] !== t3) {
    t4 = {
      enabled: t3
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const live = useGetGiftsInfinite(t2, t4);
  const isLoading = userId === "" || live.isPending;
  const isError = !isLoading && live.isError;
  let snapshotById;
  let sourceData;
  let t5;
  if ($[6] !== activeTab || $[7] !== isError || $[8] !== live.rows || $[9] !== tInbox) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      sourceData = live.rows as ReadonlyArray<GiftRowData> | undefined ?? [];
      if (isError) {
        let t6;
        if ($[13] !== activeTab || $[14] !== tInbox) {
          t6 = activeTab === "received" ? tInbox("empty.received") : tInbox("empty.sent");
          $[13] = activeTab;
          $[14] = tInbox;
          $[15] = t6;
        } else {
          t6 = $[15];
        }
        let t7;
        if ($[16] !== t6) {
          t7 = <p className="rounded-control border border-feedback-error-border/30 bg-feedback-error-accent/5 p-4 text-body text-feedback-error-content" data-testid="all-gifts-dialog-error">{t6}</p>;
          $[16] = t6;
          $[17] = t7;
        } else {
          t7 = $[17];
        }
        t5 = t7;
        break bb0;
      }
      snapshotById = new Map();
      for (const row of sourceData) {
        snapshotById.set(row.giftPda ?? row.opaqueId, row);
      }
    }
    $[6] = activeTab;
    $[7] = isError;
    $[8] = live.rows;
    $[9] = tInbox;
    $[10] = snapshotById;
    $[11] = sourceData;
    $[12] = t5;
  } else {
    snapshotById = $[10];
    sourceData = $[11];
    t5 = $[12];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  if (isLoading) {
    let t6;
    if ($[18] !== tInbox) {
      t6 = tInbox("loading");
      $[18] = tInbox;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    let t10;
    let t11;
    let t7;
    let t8;
    let t9;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Skeleton className="h-12 w-full" />;
      t8 = <Skeleton className="h-12 w-full" />;
      t9 = <Skeleton className="h-12 w-full" />;
      t10 = <Skeleton className="h-12 w-full" />;
      t11 = <Skeleton className="h-12 w-full" />;
      $[20] = t10;
      $[21] = t11;
      $[22] = t7;
      $[23] = t8;
      $[24] = t9;
    } else {
      t10 = $[20];
      t11 = $[21];
      t7 = $[22];
      t8 = $[23];
      t9 = $[24];
    }
    let t12;
    if ($[25] !== t6) {
      t12 = <output className="flex flex-col gap-2" aria-label={t6} data-testid="all-gifts-dialog-loading">{t7}{t8}{t9}{t10}{t11}</output>;
      $[25] = t6;
      $[26] = t12;
    } else {
      t12 = $[26];
    }
    return t12;
  }
  if (sourceData.length === 0) {
    let t6;
    if ($[27] !== activeTab || $[28] !== tInbox) {
      t6 = activeTab === "received" ? tInbox("empty.received") : tInbox("empty.sent");
      $[27] = activeTab;
      $[28] = tInbox;
      $[29] = t6;
    } else {
      t6 = $[29];
    }
    let t7;
    if ($[30] !== t6) {
      t7 = <p className="p-4 text-center text-body text-foreground-secondary" data-testid="all-gifts-dialog-empty">{t6}</p>;
      $[30] = t6;
      $[31] = t7;
    } else {
      t7 = $[31];
    }
    return t7;
  }
  let t6;
  if ($[32] !== activeTab || $[33] !== snapshotById) {
    t6 = t7 => {
      const {
        ref,
        id
      } = t7;
      const snapshot = snapshotById.get(id);
      return <div ref={ref}>{snapshot !== undefined && <LiveCompactGiftRow id={id} variant={activeTab} snapshot={snapshot} />}</div>;
    };
    $[32] = activeTab;
    $[33] = snapshotById;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] !== live.Items || $[36] !== t6) {
    t7 = <div className="flex flex-col gap-2 pr-1"><live.Items>{t6}</live.Items></div>;
    $[35] = live.Items;
    $[36] = t6;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  let t8;
  if ($[38] !== live.fetchNextPage || $[39] !== live.hasNextPage || $[40] !== live.isFetchingNextPage || $[41] !== t7) {
    t8 = <InfiniteList hasNextPage={live.hasNextPage} isFetchingNextPage={live.isFetchingNextPage} fetchNextPage={live.fetchNextPage} maxHeight="60vh" data-testid="all-gifts-dialog-list">{t7}</InfiniteList>;
    $[38] = live.fetchNextPage;
    $[39] = live.hasNextPage;
    $[40] = live.isFetchingNextPage;
    $[41] = t7;
    $[42] = t8;
  } else {
    t8 = $[42];
  }
  return t8;
}